import { useEffect, useState } from "react";
import { Trophy, FileSpreadsheet, Store } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { StoreRankHistory } from "@/components/StoreRankHistory";

const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
};

const lastMonths = (n = 12) => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
};

const MEDAL = ["text-amber-400", "text-slate-300", "text-orange-400"];

export function StoreLeaderboard({ contests = [], isAdmin = false }) {
  const [month, setMonth] = useState(lastMonths(1)[0]);
  const [cid, setCid] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.storeLeaderboard(month, cid).then(setData).catch(() => setData({ rows: [], totals: {} }));
  }, [month, cid]);

  return (
    <Card className="p-5 bg-card border-border mt-5" data-testid="store-leaderboard">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex-1 min-w-[220px]">
          <h3 className="font-display font-bold flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-400" /> Classifica punti vendita</h3>
          <p className="text-xs text-muted-foreground">Sedi ordinate per giocate e premi vinti nel mese selezionato{cid ? " per il concorso scelto" : ""}.</p>
        </div>
        <select data-testid="leaderboard-contest-select" value={cid} onChange={(e) => setCid(e.target.value)} className="h-10 rounded-md border border-border bg-background px-3 text-sm max-w-[220px]">
          <option value="">Tutti i concorsi</option>
          {contests.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select data-testid="leaderboard-month-select" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 rounded-md border border-border bg-background px-3 text-sm capitalize">
          {lastMonths().map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </select>
        <a href={api.storeLeaderboardExportUrl(month, cid)} data-testid="leaderboard-export-button">
          <Button variant="secondary" className="gap-2"><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
        </a>
      </div>
      {!data && <p className="text-sm text-muted-foreground">Caricamento…</p>}
      {data?.rows.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="leaderboard-empty">Nessuna giocata nel mese selezionato.</p>
      )}
      {data?.rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leaderboard-table">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="text-left py-2 pr-3">#</th>
                <th className="text-left py-2 pr-3">Punto vendita</th>
                <th className="text-right py-2 px-3">Giocate</th>
                <th className="text-right py-2 px-3">Vincite</th>
                <th className="text-right py-2 px-3">% vincita</th>
                <th className="text-right py-2 pl-3">Ritirati</th>
                <th className="text-right py-2 pl-3" title="Sconti classifica clienti riscattati in questa sede nel mese">Sconti</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={r.store_id || "none"} className="border-b border-border/50 hover:bg-secondary/30" data-testid={`leaderboard-row-${i + 1}`}>
                  <td className={`py-2 pr-3 font-bold ${r.rank ? MEDAL[i] || "text-muted-foreground" : "text-muted-foreground"}`}>{r.rank ?? "—"}</td>
                  <td className="py-2 pr-3 font-semibold flex items-center gap-2"><Store className="h-3.5 w-3.5 text-muted-foreground" /> {r.store_name}</td>
                  <td className="py-2 px-3 text-right font-mono-code">{r.plays}</td>
                  <td className="py-2 px-3 text-right font-mono-code text-emerald-400">{r.wins}</td>
                  <td className="py-2 px-3 text-right font-mono-code">{r.win_rate}%</td>
                  <td className="py-2 pl-3 text-right font-mono-code">{r.redeemed}</td>
                  <td className="py-2 pl-3 text-right font-mono-code text-amber-300" data-testid={`leaderboard-discounts-${i + 1}`}>{r.discounts_redeemed ?? 0}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="text-xs font-semibold">
              <tr>
                <td className="py-2 pr-3" colSpan={2}>Totale mese</td>
                <td className="py-2 px-3 text-right font-mono-code">{data.totals.plays}</td>
                <td className="py-2 px-3 text-right font-mono-code">{data.totals.wins}</td>
                <td className="py-2 px-3" />
                <td className="py-2 pl-3 text-right font-mono-code">{data.totals.redeemed}</td>
                <td className="py-2 pl-3 text-right font-mono-code text-amber-300" data-testid="leaderboard-discounts-total">{data.totals.discounts_redeemed ?? 0}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <StoreRankHistory contestId={cid} isAdmin={isAdmin} />
    </Card>
  );
}
